import { useMemo } from "react";
import type { Identifier } from "ra-core";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { Link } from "react-router";

import { useIsMobile } from "@/hooks/use-mobile";

import { sortGoalAudience } from "../goals/goalsAudience";
import { ListErrorState, ListRowsSkeleton } from "../design/ListStates";
import {
  computeAdvisorGoalProgress,
  computeAdvisorProgramProgress,
  computeTeamGoalProgress,
  selectApplicableProgramVersion,
} from "../incentives/incentiveProgress";
import {
  selectDirectSalesInPeriod,
  sumDirectSalesAmountMxn,
} from "../incentives/directSales";
import { APP_LOCALE } from "../misc/locale";
import {
  getCurrentMonthPeriod,
  getCurrentQuarterPeriod,
  type Period,
} from "../incentives/periods";
import { useAdvisorProgress } from "../incentives/useAdvisorProgress";
import { MobileContent } from "../layout/MobileContent";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal, LiderTeamMember } from "../types";
import { MobileHomeHeader } from "./MobileDashboard";
import {
  buildTeamFunnel,
  selectTeamExpiringLeads,
  selectTeamPosiIncompleteLeads,
} from "./liderLeadSignals";
import { LiderLeadSignals } from "./LiderLeadSignalsPanel";
import { LiderTeamProgress } from "./LiderTeamProgress";

/**
 * Almikia Epic E7 (TASK-093): the Líder BPO's team home
 * (`docs/almikia/design/B-direccion`, adapted to the team scope) -- "very
 * visual; team progress vs the team goal and each advisor vs her OWN goal;
 * where each lead is in the funnel; what is missing to reach objectives;
 * leads about to expire; POSI incomplete; operations missing documents"
 * (owner decisions section 3). Team scope comes only from
 * `lider_team_members` (RLS/FakeRest-narrowed to her own team, TASK-088) --
 * never client-side `reports_to` logic. Renders identically on desktop and
 * mobile, wired into `RoleHome.tsx` for both shells.
 */

// Generous, fixed caps (mirrors `useAdvisorProgress.ts`'s per-query cap
// convention, and the ticket's own NFR: a 15-person team with 500 open
// leads renders in under 1.5s from a fixed number of bounded queries).
const MAX_TEAM_MEMBERS = 100;
const MAX_TEAM_LEADS = 2000;

const QUARTER_MONTH_FORMAT = new Intl.DateTimeFormat(APP_LOCALE, {
  month: "short",
});

/** "jul-sep 2026" -- the best-BPO-team bar's period label, from `incentive_programs` DATA (never hardcoded). */
function formatQuarterLabel(period: Period): string {
  const start = new Date(period.start.year, period.start.month - 1, 1);
  const end = new Date(period.end.year, period.end.month - 1, 1);
  return `${QUARTER_MONTH_FORMAT.format(start)}-${QUARTER_MONTH_FORMAT.format(end)} ${period.end.year}`;
}

export const LiderDashboard = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { identity, isPending: isIdentityPending } = useGetIdentity();
  // Almikia Epic EP (TASK-107): the effective stage list (DATA CONTRACT EP
  // (8)) the team funnel groups leads into, instead of the fixed E2 order.
  const { dealStages } = useConfigurationContext();
  const liderId = identity?.id;
  const hasIdentity = Number.isInteger(liderId);
  const now = useMemo(() => new Date(), []);
  const period = useMemo(() => getCurrentMonthPeriod(now), [now]);
  const quarterPeriod = useMemo(() => getCurrentQuarterPeriod(now), [now]);

  const teamMembersResult = useGetList<LiderTeamMember>(
    "lider_team_members",
    { pagination: { page: 1, perPage: MAX_TEAM_MEMBERS } },
    { enabled: hasIdentity },
  );
  // Acceptance criteria: "in alphabetical order" -- never sorted/ranked by
  // performance (Independence guide).
  const sortedMembers = useMemo(
    () => sortGoalAudience(teamMembersResult.data ?? []),
    [teamMembersResult.data],
  );

  const teamIds = useMemo<Identifier[]>(
    () =>
      hasIdentity
        ? [liderId as Identifier, ...sortedMembers.map((member) => member.id)]
        : [],
    [hasIdentity, liderId, sortedMembers],
  );
  const hasTeamIds = teamIds.length > 0;

  const {
    programs,
    goals,
    deals: directSaleDeals,
    isPending: isProgressPending,
    error: progressError,
  } = useAdvisorProgress(teamIds, now);

  // Every non-archived team lead (any type/stage), for the funnel and
  // attention lists below -- distinct from `useAdvisorProgress`'s deals,
  // which are narrowed to direct-sale-eligible ones only.
  const teamLeadsResult = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: MAX_TEAM_LEADS },
      sort: { field: "created_at", order: "ASC" },
      filter: {
        "sales_id@in": `(${teamIds.join(",")})`,
        "archived_at@is": null,
      },
    },
    { enabled: hasTeamIds },
  );

  const advisorNameById = useMemo(() => {
    const map = new Map<Identifier, string>();
    if (hasIdentity) {
      map.set(liderId as Identifier, identity?.fullName ?? "");
    }
    for (const member of sortedMembers) {
      map.set(member.id, `${member.first_name} ${member.last_name}`.trim());
    }
    return map;
  }, [hasIdentity, liderId, identity?.fullName, sortedMembers]);

  const teamGoalProgress = hasIdentity
    ? computeTeamGoalProgress(
        liderId as Identifier,
        directSaleDeals,
        goals,
        period,
      )
    : ({ status: "no_target" } as const);

  const teamMonthSales = useMemo(
    () => selectDirectSalesInPeriod(directSaleDeals, period),
    [directSaleDeals, period],
  );
  const teamUnits = teamMonthSales.length;
  const teamAmountMxn = sumDirectSalesAmountMxn(teamMonthSales);

  const memberRows = useMemo(
    () =>
      sortedMembers.map((member) => ({
        id: member.id,
        name: `${member.first_name} ${member.last_name}`.trim(),
        goalProgress: computeAdvisorGoalProgress(
          member.id,
          directSaleDeals.filter((deal) => deal.sales_id === member.id),
          goals,
          period,
        ),
      })),
    [sortedMembers, directSaleDeals, goals, period],
  );

  const bestBpoTeam = useMemo(() => {
    if (!hasIdentity) return null;
    const models = computeAdvisorProgramProgress(programs, {
      advisorId: liderId as Identifier,
      advisorDeals: directSaleDeals,
      advisorTasks: [],
      teamDeals: directSaleDeals,
      now,
    });
    const model = models.find((m) => m.code === "best_bpo_team");
    if (!model || model.code !== "best_bpo_team") return null;
    // Same one-version-per-code selection as computeAdvisorProgramProgress,
    // so this never picks a "Nueva vigencia" version different from the one
    // the model above was actually computed from.
    const program = selectApplicableProgramVersion(
      programs.filter((p) => p.code === "best_bpo_team"),
      quarterPeriod,
      now,
    );
    return {
      programName: program?.name ?? "",
      periodLabel: formatQuarterLabel(quarterPeriod),
      teamUnits: model.teamUnits,
      teamAmountMxn: model.teamAmountMxn,
    };
  }, [hasIdentity, programs, directSaleDeals, liderId, now, quarterPeriod]);

  const funnel = useMemo(
    () =>
      buildTeamFunnel(teamLeadsResult.data ?? [], advisorNameById, dealStages),
    [teamLeadsResult.data, advisorNameById, dealStages],
  );
  const expiringLeads = useMemo(
    () =>
      selectTeamExpiringLeads(teamLeadsResult.data ?? [], advisorNameById, now),
    [teamLeadsResult.data, advisorNameById, now],
  );
  const posiIncompleteLeads = useMemo(
    () =>
      selectTeamPosiIncompleteLeads(
        teamLeadsResult.data ?? [],
        advisorNameById,
      ),
    [teamLeadsResult.data, advisorNameById],
  );

  const isSignalsPending = hasTeamIds && teamLeadsResult.isPending;
  const signalsError = teamLeadsResult.error;

  const isTopPending =
    isIdentityPending ||
    (hasIdentity && teamMembersResult.isPending) ||
    (hasTeamIds && isProgressPending);
  const topError = teamMembersResult.error ?? progressError;

  const firstName = identity?.fullName?.split(" ")[0] ?? "";

  const content = (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold">
          {translate("crm.dashboard.lider.greeting", { name: firstName })}
        </h1>
        <div className="mt-2 flex flex-wrap gap-2">
          <Link
            to="/hoy"
            className="inline-flex h-11 items-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"
          >
            {translate("crm.dashboard.lider.my_day_link")}
          </Link>
          <Link
            to="/mi-avance"
            className="inline-flex h-11 items-center rounded-xl border px-4 text-sm font-semibold"
          >
            {translate("crm.dashboard.lider.my_progress_link")}
          </Link>
        </div>
      </div>

      {topError ? (
        <ListErrorState
          message={translate("crm.common.list_error")}
          onRetry={() => teamMembersResult.refetch()}
        />
      ) : isTopPending ? (
        <ListRowsSkeleton rows={4} columns={2} />
      ) : (
        <>
          <LiderTeamProgress
            teamGoalProgress={teamGoalProgress}
            teamUnits={teamUnits}
            teamAmountMxn={teamAmountMxn}
            members={memberRows}
            bestBpoTeam={bestBpoTeam}
            showGoalActions={!isMobile}
          />

          <LiderLeadSignals
            isPending={isSignalsPending}
            error={signalsError}
            onRetry={() => teamLeadsResult.refetch()}
            funnel={funnel}
            expiringLeads={expiringLeads}
            posiIncompleteLeads={posiIncompleteLeads}
          />
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHomeHeader />
        <MobileContent>{content}</MobileContent>
      </>
    );
  }

  return content;
};
